/**
 * 项目记忆管理页面
 * 显示该项目下所有用户的记忆信息列表
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Database, Loader2, AlertCircle, RefreshCw, Trash2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { ProjectApiService } from '@/modules/agent/services/project-api'
import { memoryAdminApi } from '@/modules/memory/services/memory-admin-api'
import type { ProjectUserMemory } from '@/modules/memory/types/memory'
import { useToast } from '@/shared/hooks/useToast'

export default function ProjectMemory() {
  const { id: projectId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showError, showSuccess } = useToast()

  const [users, setUsers] = useState<ProjectUserMemory[]>([])
  const [projectName, setProjectName] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // 删除对话框状态
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<ProjectUserMemory | null>(null)
  const [deleteChatHistory, setDeleteChatHistory] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (!projectId) {
      setError('项目ID不存在')
      setIsLoading(false)
      return
    }

    loadProjectInfo()
    loadUsers()
  }, [projectId])

  const loadProjectInfo = async () => {
    if (!projectId) return

    try {
      const response = await ProjectApiService.getProject(projectId)
      if (response.success && response.data) {
        setProjectName(response.data.name)
      }
    } catch (err) {
      console.error('Failed to load project info:', err)
    }
  }

  const loadUsers = async () => {
    if (!projectId) return

    setIsLoading(true)
    setError(null)
    try {
      const data = await memoryAdminApi.getProjectUsers(projectId)
      console.log('📊 [ProjectMemory] 后端返回的用户数据:', JSON.stringify(data, null, 2))
      setUsers(data)
    } catch (err: unknown) {
      console.error('Failed to load project users:', err)
      const errorMessage = err instanceof Error ? err.message : '加载用户记忆信息失败'
      setError(errorMessage)
      showError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const formatLastSession = (dateStr?: string) => {
    if (!dateStr) return '暂无会话'
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor(diff / (1000 * 60))

    if (days > 0) return `${days}天前`
    if (hours > 0) return `${hours}小时前`
    if (minutes > 0) return `${minutes}分钟前`
    return '刚刚'
  }

  const formatEmail = (email?: string, userId?: string) => {
    if (!email || email === userId) return '-'
    return email.length > 30 ? `${email.substring(0, 30)}...` : email
  }

  const handleUserClick = (userId: string) => {
    // 跳转到该用户的记忆图谱（知识图谱）
    navigate(`/project/${projectId}/memory/${userId}`)
  }

  const handleDeleteClick = (e: React.MouseEvent, user: ProjectUserMemory) => {
    e.stopPropagation() // 阻止触发行点击
    setUserToDelete(user)
    setDeleteChatHistory(true)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!userToDelete || !projectId) return

    setIsDeleting(true)
    try {
      const result = await memoryAdminApi.deleteUserMemory(
        userToDelete.user_id,
        projectId,
        deleteChatHistory
      )
      
      if (result.success) {
        showSuccess(`已删除 ${result.entities_deleted} 个实体${deleteChatHistory ? `，${result.messages_deleted} 条消息` : ''}`)
        // 刷新用户列表
        await loadUsers()
      }
    } catch (err: unknown) {
      console.error('Failed to delete user memory:', err)
      const errorMessage = err instanceof Error ? err.message : '删除失败'
      showError(errorMessage)
    } finally {
      setIsDeleting(false)
      setDeleteDialogOpen(false)
      setUserToDelete(null)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">加载中...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              加载失败
            </CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button onClick={loadUsers} variant="outline">
                重试
              </Button>
              <Button onClick={() => navigate('/')} variant="ghost">
                返回首页
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* 顶部导航栏 */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Database className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl tracking-tight">
                {projectName ? `${projectName} - 记忆管理` : '记忆管理'}
              </span>
            </div>
          </div>
          <Button 
            onClick={loadUsers} 
            variant="outline" 
            size="sm" 
            className="gap-2"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="container mx-auto px-4 py-8">
        {/* 用户列表表格 */}
        {users.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Database className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">暂无用户记忆数据</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>用户列表</CardTitle>
              <CardDescription>
                点击列表项查看该用户的记忆图谱
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>用户昵称</TableHead>
                    <TableHead>账号</TableHead>
                    <TableHead className="text-right">实体数</TableHead>
                    <TableHead className="text-right">关系数</TableHead>
                    <TableHead className="text-right">最后会话</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((userMemory) => (
                    <TableRow
                      key={userMemory.user_id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleUserClick(userMemory.user_id)}
                    >
                      <TableCell className="font-medium">
                        {userMemory.nickname || userMemory.user_id}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatEmail(userMemory.email, userMemory.user_id)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{userMemory.entity_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{userMemory.relationship_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatLastSession(userMemory.last_session_at ?? undefined)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={(e) => handleDeleteClick(e, userMemory)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </main>

      {/* 删除确认对话框 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除记忆数据</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                即将删除用户 <span className="font-semibold text-foreground">{userToDelete?.nickname || userToDelete?.user_id}</span> 在本项目的全部记忆数据。
              </p>
              <p className="text-destructive font-medium">
                ⚠️ 此操作不可恢复！
              </p>
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="delete-chat-history"
                  checked={deleteChatHistory}
                  onCheckedChange={(checked) => setDeleteChatHistory(checked === true)}
                />
                <label
                  htmlFor="delete-chat-history"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  同时删除聊天记录
                </label>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  删除中...
                </>
              ) : (
                '确认删除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

